import * as v from "valibot";

import type {
  CaptureEventOptions,
  ChildBridge,
  RedirectToTeamGameErrorCode,
  SdkMessageByType,
  SdkMessageType,
} from "@textclub/common/bridge";
import {
  REGISTRATION_CODE_LENGTH,
  REGISTRATION_CODE_PLACEHOLDER,
  REGISTRATION_MESSAGE_CHAR_LIMIT,
  REGISTRATION_MESSAGE_UNICODE_CHAR_LIMIT,
} from "@textclub/common/constants";
import type { EntrySource } from "@textclub/common/entry-source";
import { getPlayerStateIncrement } from "@textclub/common/player-state-diagnostics";
import { createConversationId } from "@textclub/common/random-id";
import type { GamePlayer, PlayerProfile } from "@textclub/common/types";
import { validateIsRecord } from "@textclub/common/utils";

import type { JestSDK } from ".";
import { AsyncMessagingHelper } from "./async-messaging-helper";
import { IFrameChildBridge } from "./bridge";
import { instrumentSdkMethod } from "./instrumentation";
import { releaseMediaWhileHidden } from "./media-release";
import { loadMockDeps } from "./mock/loader";
import { AdsModule } from "./modules/ads";
import { DataModule } from "./modules/data";
import { HapticsModule } from "./modules/haptics";
import { InternalModule } from "./modules/internal";
import { LifecycleModule } from "./modules/lifecycle";
import type { ModuleContext } from "./modules/module-context";
import { NotificationsModule } from "./modules/notifications";
import { PaymentsModule } from "./modules/payments";
import { ReferralsModule } from "./modules/referrals";
import { SocialModule } from "./modules/social";
import { Record, parseArgs } from "./schema-utils";
import { suppressImageTouchCallout } from "./touch-callout";
import { SDK_VERSION } from "./version";

const Duration = {
  SECOND: 1000,
} as const;

const READY_TIMEOUT = 60 * Duration.SECOND;
const GET_PLAYER_SIGNED_TIMEOUT = 10 * Duration.SECOND;
const REDIRECT_TO_TEAM_GAME_TIMEOUT = 10 * Duration.SECOND;
const ENGAGEMENT_SUMMARY_OFFSETS = [10, 30, 60].map(
  (seconds) => seconds * Duration.SECOND,
);
const ENGAGEMENT_SUMMARY_INTERVAL = 60 * Duration.SECOND;
const ENGAGEMENT_SESSION_TIMEOUT = 5 * 60 * Duration.SECOND;
const PLAYER_STATE_WRITE_METHODS = new Set([
  "data.set",
  "data.delete",
  "setPlayerData",
  "setPlayerDataVal",
]);

/** Delay until the next heartbeat for a session that has run `elapsedMs`. */
const nextEngagementSummaryDelay = (elapsedMs: number) => {
  const offset = ENGAGEMENT_SUMMARY_OFFSETS.find((o) => o > elapsedMs);
  if (offset !== undefined) {
    return offset - elapsedMs;
  }
  const lastOffset = ENGAGEMENT_SUMMARY_OFFSETS.at(-1) ?? 0;
  return (
    ENGAGEMENT_SUMMARY_INTERVAL -
    ((elapsedMs - lastOffset) % ENGAGEMENT_SUMMARY_INTERVAL)
  );
};

const LoginOpts = v.optional(
  v.object({
    entryPayload: v.optional(Record),
  }),
);

const NON_ASCII = /[^\x20-\x7E]/g;

/** Length after filling the code and adding the punctuation sent by the host. */
const sentLength = (m: string) => {
  const filled = m.replace(
    REGISTRATION_CODE_PLACEHOLDER,
    "x".repeat(REGISTRATION_CODE_LENGTH),
  );
  return (/[.!?]$/.test(filled) ? filled : `${filled}.`).length;
};

// structuredClone is missing before Safari 15.4; entry sources are plain JSON.
const copyEntrySource = (source: EntrySource): EntrySource =>
  typeof structuredClone === "function"
    ? structuredClone(source)
    : (JSON.parse(JSON.stringify(source)) as EntrySource);

// The inbound reply is matched on the code, so it has to survive as its own word.
const RegistrationMessage = v.pipe(
  v.string(),
  v.trim(),
  v.transform((m) => {
    // One non-ASCII character re-encodes the whole SMS into less than half the
    // room, so past that budget they go. Accents fold to their base letter
    // first, leaving "café" readable rather than "caf".
    if (sentLength(m) <= REGISTRATION_MESSAGE_UNICODE_CHAR_LIMIT) {
      return m;
    }
    const ascii = m
      .normalize("NFD")
      .replace(/\p{Diacritic}/gu, "")
      .replace(NON_ASCII, "")
      .replace(/ {2,}/g, " ")
      .trim();
    return ascii === m ? m : ascii;
  }),
  v.nonEmpty("message must not be empty"),
  v.includes(
    REGISTRATION_CODE_PLACEHOLDER,
    `message must contain ${REGISTRATION_CODE_PLACEHOLDER}`,
  ),
  v.check(
    // Only the first placeholder is filled in; any brace left over is texted verbatim.
    (m) => !/[{}]/.test(m.replace(REGISTRATION_CODE_PLACEHOLDER, "")),
    `message must contain ${REGISTRATION_CODE_PLACEHOLDER} once and no other placeholder`,
  ),
  v.check((m) => {
    const at = m.indexOf(REGISTRATION_CODE_PLACEHOLDER);
    return (
      !/[\w+]$/.test(m.slice(0, at)) &&
      !/^[\w+]/.test(m.slice(at + REGISTRATION_CODE_PLACEHOLDER.length))
    );
  }, `message must leave a space around ${REGISTRATION_CODE_PLACEHOLDER}`),
  v.check(
    (m) => sentLength(m) <= REGISTRATION_MESSAGE_CHAR_LIMIT,
    `message must be at most ${REGISTRATION_MESSAGE_CHAR_LIMIT} characters once the code is filled in`,
  ),
);

const ShowRegistrationOverlayOpts = v.optional(
  v.object({
    theme: v.optional(v.picklist(["light", "dark"])),
    onClose: v.optional(
      v.pipe(
        v.unknown(),
        v.check((x) => typeof x === "function", "expected a function"),
      ),
    ),
    entryPayload: v.optional(Record),
    message: v.optional(RegistrationMessage),
  }),
);

const UnscheduleNotificationOpts = v.object({
  identifier: v.string(),
});

const CaptureEventOptionsSchema = v.object({
  send_instantly: v.optional(v.boolean()),
  transport: v.optional(v.picklist(["XHR", "fetch", "sendBeacon"])),
});

const CaptureEventArgs = v.tuple([
  v.string(),
  v.optional(Record),
  v.optional(CaptureEventOptionsSchema),
]);

const RedirectToTeamGameOpts = v.object({
  gameSlug: v.string(),
  entryPayload: v.optional(Record),
  skipGameExitConfirm: v.optional(v.boolean()),
});

type MessageHandlers = Partial<{
  [K in SdkMessageType]: (message: SdkMessageByType<K>) => Promise<unknown>;
}>;

/**
 * The main SDK class for integrating games with the Jest platform.
 * Handles player management, notifications, and analytics within an iframe context.
 * @example
 * ```typescript
 * const sdk = new Sdk();
 * await sdk.isReady();
 * sdk.login("Welcome! Your code is {{registrationCode}}", "Register to Play");
 * ```
 */

// Use this to flip our unintialized sdk to an initialized sdk
interface Initialized {
  _player: GamePlayer;
}

/** @internal */
export { GetLoginLinkResponseErrors } from "./login-errors";

export class Sdk implements JestSDK {
  private resolveReady: () => void = () => {};
  private rejectReady: (error: Error) => void = () => {};
  private readyPromise: Promise<void>;
  private initMessageInterval: number;
  private readyTimeoutHandle: number;
  private bridge: ChildBridge;

  private playerProfile: PlayerProfile | null = null;
  private entrySource: EntrySource = { type: "unknown" };

  // I couldn't get the typeguard to work on our player object without making this public
  _player: GamePlayer | null = null;

  private asyncBridgeHelper: AsyncMessagingHelper;
  private messageHandlers: MessageHandlers = {};

  readonly lifecycle: LifecycleModule;
  readonly haptics: HapticsModule;
  // Data module - owns all player data state and operations
  readonly data: DataModule;
  // Notifications module - owns notification scheduling operations
  readonly notifications: NotificationsModule;
  // Payments module - owns purchase flow operations
  readonly payments: PaymentsModule;
  // Referrals module - owns referral operations
  readonly referrals: ReferralsModule;
  readonly ads: AdsModule;
  // Social module - owns social operations
  readonly social: SocialModule;
  // Internal module - non-public SDK operations
  readonly internal: InternalModule;

  // Diagnostics.
  private clientSdkContextId: string;

  /**
   * Creates an instance of the SDK with an optional bridge for communication.
   * Automatically starts initialization by sending "Initialized" messages to the parent window.
   * @param opts - Configuration options for the SDK.
   */
  constructor(
    opts: {
      autoLoginReminders?: boolean;
    } = {},
    clientOpts?: {
      bridge?: ChildBridge;
    },
  ) {
    let sdkContextId: string;
    let windowLocationHref: string;
    if (clientOpts?.bridge) {
      this.bridge = clientOpts.bridge;
      sdkContextId = "mock";
      windowLocationHref = "mock";
    } else {
      const sidParam = new URLSearchParams(window.location.search).get("sid");
      if (!sidParam) {
        throw new Error("No SDK context ID found");
      }
      sdkContextId = sidParam;
      windowLocationHref = window.location.href;
      this.bridge = new IFrameChildBridge(sdkContextId, SDK_VERSION);
    }

    this.clientSdkContextId = sdkContextId;

    this.asyncBridgeHelper = new AsyncMessagingHelper();

    // Capture diagnostic info at init time for sdk_context_mismatch debugging
    const initDiagnostics = {
      sdkContextId,
      windowLocationHref,
    };

    const registerMessageHandler: ModuleContext["registerMessageHandlers"] = (
      handlers,
    ) => {
      for (const [type, handler] of Object.entries(handlers)) {
        if (!handler) {
          continue;
        }
        const messageType = type as SdkMessageType;
        if (this.messageHandlers[messageType]) {
          throw new Error(`Duplicate handler for ${messageType}`);
        }
        const handlers = this.messageHandlers as Partial<
          Record<SdkMessageType, (message: never) => Promise<unknown>>
        >;
        const handle = handler as (message: never) => unknown;
        handlers[messageType] = async (message) => {
          await handle(message);
        };
      }
    };

    registerMessageHandler({
      SetPlayer: (message) => {
        if (!this._player && message.entrySource) {
          this.entrySource = copyEntrySource(message.entrySource);
        }
        this.initPlayer(message.player);
        this.playerProfile = message.profile;
      },
      GetPlayerSignedResponse: (message) => {
        this.asyncBridgeHelper.resolveWaiting(message);
      },
      PlatformRegistrationOverlayClosed: (message) => {
        this.asyncBridgeHelper.resolveWaiting(message);
      },
      PlatformLoginClosed: (message) => {
        this.asyncBridgeHelper.resolveWaiting(message);
      },
      RedirectToTeamGameResponse: (message) => {
        this.asyncBridgeHelper.resolveWaiting(message);
      },
    });

    const moduleContext: ModuleContext = {
      sendMessage: (message) => this.bridge.sendMessage(message),
      waitForNextMessage: (type, conversationId, timeoutMs) =>
        this.asyncBridgeHelper.waitForNextMessage(
          type,
          conversationId,
          timeoutMs,
        ),
      resolveWaiting: (message) =>
        this.asyncBridgeHelper.resolveWaiting(message),
      instrumentMethod: (contextOpts) => this.instrumentMethod(contextOpts),
      assertInitialized: () => this.ensureInitialized(),
      isLoggedIn: () => {
        this.ensureInitialized();
        return this._player.registered;
      },
      registerMessageHandlers: registerMessageHandler,
      getPlayerProfile: () => {
        this.ensureInitialized();
        return this.playerProfile;
      },
    };

    // Bootstrap modules
    this.lifecycle = new LifecycleModule(moduleContext);
    this.haptics = new HapticsModule(moduleContext);
    this.data = new DataModule(moduleContext);
    this.notifications = new NotificationsModule(moduleContext);
    this.payments = new PaymentsModule(moduleContext);
    this.referrals = new ReferralsModule(moduleContext);
    this.ads = new AdsModule(moduleContext);
    this.social = new SocialModule(moduleContext);
    this.internal = new InternalModule(moduleContext);
    releaseMediaWhileHidden(this.lifecycle);
    suppressImageTouchCallout();

    this.initMessageInterval = setInterval(() => {
      this.bridge.sendMessage({
        type: "Initialized",
        ...initDiagnostics,
        autoLoginReminders: opts.autoLoginReminders,
      });
    }, 50);

    this.readyPromise = new Promise((resolve, reject) => {
      this.resolveReady = resolve;
      this.rejectReady = reject;
    });

    this.readyTimeoutHandle = setTimeout(() => {
      this.rejectReady(new Error("Timeout waiting for player init"));
      clearInterval(this.initMessageInterval);
    }, READY_TIMEOUT);

    this.bridge.onMessage = (message) => {
      this.handleMessage(message).catch((error) => {
        console.error("Error handling message", message, error);
      });
    };

    // Listen for BFCache restoration to help diagnose sdk_context_mismatch
    if (typeof window !== "undefined" && window.addEventListener) {
      window.addEventListener("pageshow", (event) => {
        if (event.persisted) {
          this.bridge.sendMessage({
            type: "IframeBfcacheRestore",
            sdkContextId,
            // Refetch current URL instead of using cached value from init
            windowLocationHref: window.location.href,
          });
        }
      });
    }
  }

  private emitSdkMethodCalled(
    method: string,
    args: string,
    updateId?: number,
    stateIncrement?: number | null,
  ) {
    const clientSdkContextId = this.clientSdkContextId;
    const clientIframeUrl =
      typeof window !== "undefined" ? window.location.href : "unknown";

    this.bridge.sendMessage({
      type: "SdkMethodCalled",
      method,
      args,
      clientSdkContextId,
      clientIframeUrl,
      updateId,
      stateIncrement,
    });
  }

  private emitSdkMethodError(method: string, args: string, error: unknown) {
    this.bridge.sendMessage({
      type: "SdkMethodError",
      method,
      args,
      error:
        typeof error === "string"
          ? error
          : "toString" in (error as object)
            ? (error as Error).toString()
            : "Unknown error",
    });
  }

  protected instrumentMethod<T>(opts: {
    method: string;
    args: unknown;
    fn: () => T;
    skipAnalytics?: boolean;
  }): T {
    const { method, args, fn, skipAnalytics } = opts;
    const isPlayerStateWrite = PLAYER_STATE_WRITE_METHODS.has(method);
    const methodArgs: unknown[] = Array.isArray(args)
      ? (args as unknown[])
      : [];
    const first = methodArgs[0];
    const second = methodArgs[1];
    const keys =
      typeof first === "string"
        ? [first]
        : validateIsRecord(first)
          ? Object.keys(first)
          : [];
    const stateIncrement = isPlayerStateWrite
      ? getPlayerStateIncrement(
          typeof first === "string" && first === "meta"
            ? { meta: second }
            : validateIsRecord(first)
              ? first
              : {},
        )
      : undefined;
    const updateId = isPlayerStateWrite ? this.data.nextUpdateId() : undefined;

    let argString: string;
    try {
      argString = JSON.stringify(isPlayerStateWrite ? [{ keys }] : args);
    } catch {
      argString = "Could not serialize args";
    }

    if (!skipAnalytics) {
      this.emitSdkMethodCalled(method, argString, updateId, stateIncrement);
    }

    try {
      const result = fn();

      if (this.isPromiseLike(result)) {
        return Promise.resolve(result).catch((error) => {
          this.emitSdkMethodError(method, argString, error);
          throw error;
        }) as T;
      }

      return result;
    } catch (error) {
      this.emitSdkMethodError(method, argString, error);
      throw error;
    }
  }

  private isPromiseLike(value: unknown): value is Promise<unknown> {
    return (
      typeof value === "object" &&
      value !== null &&
      typeof (value as { then?: unknown }).then === "function"
    );
  }

  private async handleMessage<T extends SdkMessageType>(
    message: SdkMessageByType<T>,
  ): Promise<boolean> {
    const handler = this.messageHandlers[message.type];

    if (!handler) {
      return false;
    }

    const result = handler(message);
    if (result instanceof Promise) {
      await result;
    }

    return true;
  }

  private initPlayer(player: GamePlayer) {
    clearTimeout(this.readyTimeoutHandle);
    clearInterval(this.initMessageInterval);
    this._player = player;
    // Initialize data module with defaults and stored data
    this.data.initialize(this._player.playerData);
    this.resolveReady();

    // Set up first interaction listener
    this.setupFirstInteractionListener();
    this.setupEngagementTracking();
  }

  private setupFirstInteractionListener() {
    if (typeof document === "undefined" || typeof window === "undefined") {
      return;
    }

    const events = ["pointerdown", "touchstart", "click", "keydown"] as const;
    const opts = { capture: true } as const;
    let captured = false;
    let rafHandle = 0;
    const capture = () => {
      if (captured) {
        return;
      }
      captured = true;
      for (const event of events) {
        document.removeEventListener(event, capture, opts);
      }
      cancelAnimationFrame(rafHandle);
      this.bridge.sendMessage({
        type: "OnboardingCaptureEvent",
        event: "first_interaction",
      });
    };

    for (const event of events) {
      document.addEventListener(event, capture, opts);
    }

    // Some games wrap their canvas in a nested iframe (typically cross-origin)
    // and load the actual game from there. Input events targeting elements
    // inside such an iframe are dispatched within its own document tree and
    // never bubble out to ours, so the listeners above won't see them. Cross-
    // origin also rules out reaching in via contentDocument.
    //
    // The one signal that *does* cross the boundary is document.activeElement
    // — tapping an iframe (cross-origin or not) makes it active. If a nested
    // iframe is already in our DOM at SDK init, poll for a transition into
    // an iframe and treat that as the first interaction. Tracking the
    // previous value avoids treating an autofocused iframe at init as an
    // interaction.
    //
    // Caveats:
    // - A programmatic iframe.focus() call from build code would look
    //   identical to a user tap and falsely fire. We accept that — game
    //   builds practically never call .focus() on their canvas frame, and
    //   the detection is otherwise impossible across the cross-origin wall
    //   without cooperation from the inner iframe.
    // - We only check for an iframe at SDK init. Builds that inject their
    //   nested iframe later are not picked up — fine in practice since
    //   builds typically declare the iframe in their initial HTML. If that
    //   assumption breaks, gate this on a MutationObserver instead.
    if (!document.querySelector("iframe")) {
      return;
    }
    let last = document.activeElement;
    const tick = () => {
      if (captured) {
        return;
      }
      const curr = document.activeElement;
      if (curr !== last && curr?.tagName === "IFRAME") {
        capture();
        return;
      }
      last = curr;
      rafHandle = requestAnimationFrame(tick);
    };
    rafHandle = requestAnimationFrame(tick);
  }

  /**
   * Emit an `engagement_summary` at {@link ENGAGEMENT_SUMMARY_OFFSETS} into the
   * session and every {@link ENGAGEMENT_SUMMARY_INTERVAL} after that, only when
   * a metric moved since the last emission. Counts reset after
   * {@link ENGAGEMENT_SESSION_TIMEOUT} of inactivity under a new sdk_session_id.
   */
  private setupEngagementTracking() {
    if (typeof document === "undefined" || typeof window === "undefined") {
      return;
    }

    let counts = { taps: 0, keypresses: 0, nestedFrameTaps: 0 };
    let emitted = { ...counts };
    let sessionId = createConversationId();
    let sessionStartedAt = Date.now();
    let lastActivityAt = sessionStartedAt;

    let summaryTimer: ReturnType<typeof setTimeout> | undefined;
    const scheduleSummary = () => {
      clearTimeout(summaryTimer);
      summaryTimer = setTimeout(
        () => {
          emitSummary();
          scheduleSummary();
        },
        nextEngagementSummaryDelay(Date.now() - sessionStartedAt),
      );
    };

    const recordActivity = () => {
      const now = Date.now();
      if (now - lastActivityAt > ENGAGEMENT_SESSION_TIMEOUT) {
        sessionId = createConversationId();
        sessionStartedAt = now;
        counts = { taps: 0, keypresses: 0, nestedFrameTaps: 0 };
        emitted = { ...counts };
        scheduleSummary();
      }
      lastActivityAt = now;
    };

    // A touch tap fires both touchstart and pointerdown; listen to pointer
    // events alone where supported to avoid counting it twice.
    const tapEvents =
      typeof PointerEvent === "undefined"
        ? (["touchstart", "mousedown"] as const)
        : (["pointerdown"] as const);
    const tapOpts = { capture: true, passive: true } as const;
    const keyOpts = { capture: true } as const;

    const onTap = () => {
      recordActivity();
      counts.taps += 1;
    };
    const onKey = () => {
      recordActivity();
      counts.keypresses += 1;
    };
    for (const event of tapEvents) {
      document.addEventListener(event, onTap, tapOpts);
    }
    document.addEventListener("keydown", onKey, keyOpts);

    // Taps in a nested cross-origin frame never reach us; count focus
    // transitions into the iframe instead (see setupFirstInteractionListener).
    let lastActive = document.activeElement;
    let rafHandle = 0;
    const pollNestedFrame = () => {
      const curr = document.activeElement;
      if (curr !== lastActive && curr?.tagName === "IFRAME") {
        recordActivity();
        counts.nestedFrameTaps += 1;
      }
      lastActive = curr;
      rafHandle = requestAnimationFrame(pollNestedFrame);
    };
    if (document.querySelector("iframe")) {
      rafHandle = requestAnimationFrame(pollNestedFrame);
    }

    const emitSummary = () => {
      if (document.visibilityState === "hidden") {
        return;
      }
      const tapsDelta = counts.taps - emitted.taps;
      const keypressesDelta = counts.keypresses - emitted.keypresses;
      const nestedFrameTapsDelta =
        counts.nestedFrameTaps - emitted.nestedFrameTaps;
      if (
        tapsDelta === 0 &&
        keypressesDelta === 0 &&
        nestedFrameTapsDelta === 0
      ) {
        return;
      }
      this.bridge.sendMessage({
        type: "OnboardingCaptureEvent",
        event: "engagement_summary",
        properties: {
          sdk_session_id: sessionId,
          elapsed_ms: Date.now() - sessionStartedAt,
          taps: counts.taps,
          keypresses: counts.keypresses,
          nested_frame_taps: counts.nestedFrameTaps,
          total_engagements:
            counts.taps + counts.keypresses + counts.nestedFrameTaps,
          taps_delta: tapsDelta,
          keypresses_delta: keypressesDelta,
          nested_frame_taps_delta: nestedFrameTapsDelta,
        },
      });
      emitted = { ...counts };
    };

    scheduleSummary();

    const cleanup = () => {
      clearTimeout(summaryTimer);
      cancelAnimationFrame(rafHandle);
      for (const event of tapEvents) {
        document.removeEventListener(event, onTap, tapOpts);
      }
      document.removeEventListener("keydown", onKey, keyOpts);
      window.removeEventListener("pagehide", cleanup);
    };
    window.addEventListener("pagehide", cleanup);
  }

  private ensureInitialized(): asserts this is Initialized {
    if (!this._player || this._player === null) {
      throw new Error(
        "Player not initialized. Await `JestSDK.init()` before doing anything.",
      );
    }
  }

  @instrumentSdkMethod()
  getEntryPayload(): Record<string, unknown> {
    this.ensureInitialized();

    return this._player.entryPayload;
  }

  @instrumentSdkMethod()
  getEntrySource(): EntrySource {
    this.ensureInitialized();
    return copyEntrySource(this.entrySource);
  }

  @instrumentSdkMethod()
  getPlayer(): {
    playerId: string;
    registered: boolean;
    username: string | null;
    avatarUrl: string | null;
  } {
    this.ensureInitialized();
    return {
      playerId: this._player.playerId,
      registered: this._player.registered,
      username: this._player.username,
      avatarUrl: this._player.avatarUrl,
    };
  }

  @instrumentSdkMethod()
  async getPlayerSigned(): Promise<{
    player: {
      playerId: string;
      registered: boolean;
      username: string | null;
      avatarUrl: string | null;
    };
    playerSigned: string;
  }> {
    this.ensureInitialized();

    const conversationId = createConversationId();

    this.bridge.sendMessage({
      conversationId,
      type: "GetPlayerSigned",
    });

    const response = await this.asyncBridgeHelper.waitForNextMessage(
      "GetPlayerSignedResponse",
      conversationId,
      GET_PLAYER_SIGNED_TIMEOUT,
    );

    if (!response.success) {
      throw new Error("Failed to fetch signed player");
    }

    return {
      player: response.player,
      playerSigned: response.playerSigned,
    };
  }

  @instrumentSdkMethod()
  getPlayerDataVal(key: string): unknown {
    return this.data._coreGet(key);
  }

  @instrumentSdkMethod()
  getPlayerData(): Record<string, unknown> {
    return this.data._coreGetAll();
  }

  @instrumentSdkMethod()
  setPlayerDataVal(key: string, value: unknown): void {
    if (typeof key !== "string") {
      throw new Error("INVALID_ARGUMENTS: key must be a string");
    }

    this.data._coreSet({ [key]: value });
  }

  @instrumentSdkMethod()
  setPlayerData(partial: Record<string, unknown>): void {
    this.data._coreSet(partial);
  }

  private _loadingComplete = false;

  @instrumentSdkMethod()
  setLoadingProgress(progress: number): void {
    const clamped = Math.round(Math.max(0, Math.min(100, progress)));
    if (clamped === 100) {
      this._loadingComplete = true;
    }
    this.bridge.sendMessage({
      type: "SetLoadingProgress",
      progress: clamped,
    });
  }

  private _gameLoadedSent = false;

  @instrumentSdkMethod()
  markGameLoaded(): void {
    if (this._gameLoadedSent) {
      return;
    }
    this._gameLoadedSent = true;

    // A loaded game implies loading is finished, so dismiss the loading
    // overlay unless the game already drove progress to 100 itself.
    if (!this._loadingComplete) {
      this._loadingComplete = true;
      this.bridge.sendMessage({
        type: "SetLoadingProgress",
        progress: 100,
      });
    }

    this.bridge.sendMessage({ type: "GameLoaded" });
  }

  // Retired; instrumentation stays so remaining callers are still visible.
  @instrumentSdkMethod()
  markFirstMilestone(): void {}

  static async mock(opts: { autoLoginReminders?: boolean } = {}) {
    const { MockBridge, DebugMenu } = await loadMockDeps();
    const mockBridge = new MockBridge();
    new DebugMenu(mockBridge);
    return new Sdk(opts, { bridge: mockBridge });
  }

  /**
   * Waits for the SDK to be fully initialized with player data.
   * @returns A promise that resolves when the player is set.
   * @throws {Error} If initialization times out after 1 second.
   */
  @instrumentSdkMethod()
  async isReady(): Promise<void> {
    return this.readyPromise;
  }

  @instrumentSdkMethod()
  async login(opts?: {
    entryPayload?: Record<string, unknown>;
  }): Promise<void> {
    parseArgs(LoginOpts, opts);

    this.ensureInitialized();
    if (this._player.registered) {
      // Nothing to log into; resolve immediately for already-registered players.
      return;
    }

    const conversationId = createConversationId();

    this.bridge.sendMessage({
      type: "BeginPlatformLogin",
      entryPayload: JSON.stringify(opts?.entryPayload),
      conversationId,
    });

    await this.asyncBridgeHelper.waitForNextMessage(
      "PlatformLoginClosed",
      conversationId,
      null,
    );
  }

  @instrumentSdkMethod()
  showRegistrationOverlay(opts?: {
    theme?: "light" | "dark";
    onClose?: () => void;
    entryPayload?: Record<string, unknown>;
    message?: string;
  }): {
    loginButtonAction: () => void;
    closeButtonAction: () => void;
  } {
    const parsed = parseArgs(ShowRegistrationOverlayOpts, opts);

    this.ensureInitialized();

    if (this._player.registered) {
      throw new Error("Player already logged in");
    }

    const conversationId = createConversationId();

    this.bridge.sendMessage({
      type: "BeginPlatformRegistrationOverlay",
      theme: opts?.theme,
      entryPayload: JSON.stringify(opts?.entryPayload),
      registrationMessage: parsed?.message ?? null,
      conversationId: conversationId,
    });

    void this.asyncBridgeHelper
      .waitForNextMessage(
        "PlatformRegistrationOverlayClosed",
        conversationId,
        null, // no timeout
      )
      .then(() => {
        opts?.onClose?.();
      });

    return {
      loginButtonAction: () => {
        this.bridge.sendMessage({
          conversationId,
          type: "PlatformRegistrationOverlayLogin",
        });
      },
      closeButtonAction: () => {
        this.bridge.sendMessage({
          conversationId,
          type: "DismissPlatformRegistrationOverlay",
        });
      },
    };
  }

  @instrumentSdkMethod()
  unscheduleNotification(options: { identifier: string }) {
    parseArgs(UnscheduleNotificationOpts, options);

    this.ensureInitialized();

    this.bridge.sendMessage({
      type: "UnscheduleNotification",
      identifier: options.identifier,
    });
  }

  @instrumentSdkMethod()
  captureEvent(
    eventName: string,
    properties?: Record<string, unknown>,
    options?: CaptureEventOptions,
  ) {
    const [, , parsedOptions] = parseArgs(CaptureEventArgs, [
      eventName,
      properties,
      options,
    ]);

    this.bridge.sendMessage({
      type: "CaptureEvent",
      options: parsedOptions,
      event: eventName,
      properties: {
        ...(properties || {}),
        sdk_version: SDK_VERSION,
      },
    });
  }

  @instrumentSdkMethod()
  async redirectToTeamGame(options: {
    gameSlug: string;
    entryPayload?: Record<string, unknown>;
    skipGameExitConfirm?: boolean;
  }): Promise<
    | { result: "success" }
    | { result: "error"; error: RedirectToTeamGameErrorCode }
  > {
    parseArgs(RedirectToTeamGameOpts, options);

    this.ensureInitialized();

    const conversationId = createConversationId();

    this.bridge.sendMessage({
      type: "RedirectToTeamGame",
      conversationId,
      gameSlug: options.gameSlug,
      entryPayload: JSON.stringify(options.entryPayload),
      skipGameExitConfirm: options.skipGameExitConfirm ?? false,
    });

    // A response timeout resolves as internal_error per the documented contract, rather than throwing.
    const response = await this.asyncBridgeHelper
      .waitForNextMessage(
        "RedirectToTeamGameResponse",
        conversationId,
        REDIRECT_TO_TEAM_GAME_TIMEOUT,
      )
      .catch(() => null);

    if (!response) {
      return { result: "error", error: "internal_error" };
    }

    if (!response.success) {
      return { result: "error", error: response.error };
    }

    return { result: "success" };
  }

  /**
   * @deprecated Use `internal.redirectToFlagshipGame` instead.
   */
  @instrumentSdkMethod()
  redirectToFlagshipGame(options?: {
    entryPayload?: Record<string, unknown>;
  }): void {
    this.internal._coreRedirectToFlagshipGame(options);
  }

  /**
   * @deprecated Use `internal.redirectToGame` instead.
   */
  @instrumentSdkMethod()
  redirectToGame(options: {
    gameSlug: string;
    entryPayload?: Record<string, unknown>;
    skipGameExitConfirm?: boolean;
  }): void {
    this.internal._coreRedirectToGame(options);
  }

  /**
   * @deprecated Use `internal.openPrivacyPolicy` instead.
   */
  @instrumentSdkMethod()
  openPrivacyPolicy() {
    this.internal._coreOpenPrivacyPolicy();
  }

  /**
   * @deprecated Use `internal.openTermsOfService` instead.
   */
  @instrumentSdkMethod()
  openTermsOfService() {
    this.internal._coreOpenTermsOfService();
  }

  /**
   * @deprecated Use `internal.openCopyright` instead.
   */
  @instrumentSdkMethod()
  openCopyright() {
    this.internal._coreOpenCopyright();
  }

  @instrumentSdkMethod()
  async flush(): Promise<void> {
    return this.data._coreFlush();
  }
}
